/**
 * Datasource local Reminders: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { Reminder } from '@/core/domain';

const CACHE_KEY_PREFIX = 'retornaar:reminders';

export interface ReminderLocalDataSource {
  getById(id: string): Promise<Reminder | null>;
  getAllByUserId(userId: string): Promise<Reminder[]>;
  getAll(userId: string): Promise<Reminder[]>;
  cacheAll(userId: string, reminders: Reminder[]): Promise<void>;
  create(reminder: Reminder): Promise<Reminder>;
  update(id: string, reminder: Partial<Reminder>): Promise<Reminder>;
  delete(id: string): Promise<void>;
  clear(userId?: string): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'reminders' });

export class ReminderLocalForageDataSource implements ReminderLocalDataSource {
  private cacheKey(userId: string) {
    return `${CACHE_KEY_PREFIX}:${userId}`;
  }

  private async getMap(userId: string): Promise<Record<string, Reminder>> {
    const raw = await store.getItem<Record<string, Reminder>>(this.cacheKey(userId));
    return raw ?? {};
  }

  private async setMap(userId: string, map: Record<string, Reminder>): Promise<void> {
    await store.setItem(this.cacheKey(userId), map);
  }

  async getById(id: string): Promise<Reminder | null> {
    const keys = await store.keys();
    const reminderKeys = keys.filter(
      (k) => typeof k === 'string' && k.startsWith(CACHE_KEY_PREFIX)
    );
    for (const key of reminderKeys) {
      const map = await store.getItem<Record<string, Reminder>>(key);
      if (map?.[id]) return map[id];
    }
    return null;
  }

  async getAllByUserId(userId: string): Promise<Reminder[]> {
    const map = await this.getMap(userId);
    return Object.values(map);
  }

  async getAll(userId: string): Promise<Reminder[]> {
    return this.getAllByUserId(userId);
  }

  async cacheAll(userId: string, reminders: Reminder[]): Promise<void> {
    const map: Record<string, Reminder> = {};
    for (const r of reminders) {
      map[r.id] = r;
    }
    await this.setMap(userId, map);
  }

  async create(reminder: Reminder): Promise<Reminder> {
    const map = await this.getMap(reminder.user_id);
    map[reminder.id] = reminder;
    await this.setMap(reminder.user_id, map);
    return reminder;
  }

  async update(id: string, reminder: Partial<Reminder>): Promise<Reminder> {
    const r = await this.getById(id);
    if (!r) throw new Error('Reminder not found');
    const map = await this.getMap(r.user_id);
    const current = map[id];
    if (!current) throw new Error('Reminder not found');
    const updated = { ...current, ...reminder, id };
    map[id] = updated;
    await this.setMap(r.user_id, map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const r = await this.getById(id);
    if (!r) return;
    const map = await this.getMap(r.user_id);
    delete map[id];
    await this.setMap(r.user_id, map);
  }

  async clear(userId?: string): Promise<void> {
    if (userId) {
      await store.removeItem(this.cacheKey(userId));
    } else {
      const keys = await store.keys();
      for (const key of keys) {
        if (typeof key === 'string' && key.startsWith(CACHE_KEY_PREFIX)) {
          await store.removeItem(key);
        }
      }
    }
  }
}
